import { ListHeader } from '@/components/ListHeader'
import { type ReactNode } from 'react'
import styles from './DocumentRoot.module.css'
import { useScrollOffsets } from '@/hooks/element-hooks'
import {
  StickyViewHeader,
  StickyViewHeaderMain,
  StickyViewHeaderSpacer,
} from '@/components/StickyViewHeader'
import { PageTip } from '@/components/PageTip'
import { DocumentsListFrame } from './DocumentsList'
import { useDropzone } from '@/hooks/dropzone-hook'
import { Button } from '@/components/Button'
import { FileButton } from '@/components/FileButton'
import { byDesign } from '@/infra/design'
import imgUploadDocsUrl from '@/assets/misc/img_upload-docs.svg'
import DragAndUploadIllustration from '@/components/SpotIllustration/DragAndUploadIllustration'

export interface DocumentRootProps {
  heading: ReactNode
  documentsCount?: number
  /**
   * The documents list, or its loading, error or empty state.
   */
  content: ReactNode
  canUploadFiles: boolean
  isWorkspaceSelected: boolean
  currentWorkspaceName: string
  onQueueFilesUpload: (files: FileList) => void
  onSelectCurrentWorkspace: () => unknown
  onDragEnter: () => void
  onDragLeave: () => void
  onDrop: (files: FileList) => void
  /**
   * Whether the workspace wants a corporate look, which hides illustrations.
   */
  corporateMode: boolean
}

export function DocumentRoot({
  heading,
  documentsCount,
  content,
  canUploadFiles,
  isWorkspaceSelected,
  currentWorkspaceName,
  onQueueFilesUpload,
  onSelectCurrentWorkspace,
  onDragEnter,
  onDragLeave,
  onDrop,
  corporateMode,
}: DocumentRootProps) {
  const { connectRef: scrollRef, scrollYStart: scrollOffset } =
    useScrollOffsets()
  const { dropzoneProps } = useDropzone<HTMLDivElement>({
    onFilesDropped: onDrop,
    onEnter: onDragEnter,
    onLeave: onDragLeave,
  })

  return (
    <div ref={scrollRef} className={styles.root}>
      <div {...dropzoneProps} className={styles.dragAndDropOverlay}>
        <StickyViewHeader scrollOffset={scrollOffset}>
          <StickyViewHeaderMain>{heading}</StickyViewHeaderMain>
          <StickyViewHeaderSpacer />
        </StickyViewHeader>

        <div className={styles.split}>
          <div className={styles.content}>
            <DocumentsListFrame
              header={<ListHeader count={documentsCount}>Documents</ListHeader>}
            >
              {content}
            </DocumentsListFrame>
          </div>
          <div className={styles.side}>
            {canUploadFiles &&
              (isWorkspaceSelected ? (
                <PageTip
                  corporateMode={corporateMode}
                  title="Want to upload multiple documents fast?"
                  illustrationUrl={imgUploadDocsUrl}
                  illustration={byDesign(undefined, DragAndUploadIllustration)}
                  button={
                    <FileButton
                      multiple={true}
                      onChange={onQueueFilesUpload}
                      data-test="upload-document-input-classic"
                    >
                      <Button label="Browse Files" variant="primary" />
                    </FileButton>
                  }
                >
                  Simply drag and drop anywhere on this screen to upload. Or,
                  click the button below to lookup files
                </PageTip>
              ) : (
                <PageTip
                  corporateMode={corporateMode}
                  title="Select a workspace to upload documents"
                  illustrationUrl={imgUploadDocsUrl}
                  illustration={byDesign(undefined, DragAndUploadIllustration)}
                  button={
                    <Button
                      label={`Switch to ${currentWorkspaceName}`}
                      onPress={onSelectCurrentWorkspace}
                      variant="primary"
                    />
                  }
                >
                  You're viewing documents in all your workspaces. Select a
                  workspace or switch bellow to upload more
                </PageTip>
              ))}
          </div>
        </div>
      </div>
    </div>
  )
}
